import { useState } from 'react'
import { ReviewerRole } from '../ReviewsWizard/types'
import Styles from './CreateReviewActionConfigModal.module.scss'
import { ModalTitle } from '../ReviewsWizard/ModalTitle'
import { ReviewersFields } from '../ReviewsWizard/AddReviewers/ReviewersFields'
import { AddReviewerButton } from '../ReviewsWizard/AddReviewers/AddReviewerButton'
import { AddApproverButton } from '../ReviewsWizard/AddReviewers/AddApproverButton'
import { AddViewerButton } from '../ReviewsWizard/AddReviewers/AddViewerButton'
import { RecipientsSelectorValueType } from 'types'
import { useSourceData } from '../ReviewsWizard/SourceDataProvider/useSourceData'
import { SourceDataProvider } from '../ReviewsWizard/SourceDataProvider/SourceDataProvider'
import AddReviewersStyles from '../ReviewsWizard/AddReviewers/AddReviewers.module.scss'
import { Field } from '../ReviewsWizard/AddReviewers/Field'
import cx from 'classnames'
import { ProseStateProvider } from '../../common/ProseInput/ProseStateContext'
import { Toolbar } from '../../common/ProseInput/components/Toolbar'
import { ProseInput } from '../../common/ProseInput/ProseInput'
import { ProseState } from '../../common/ProseInput/ProseState'
import { stopPropagation } from '../../utils'
import { DueDatePicker as BaseDueDatePicker } from '../DueDatePicker/DueDatePicker'
import dayjs from 'dayjs'
import { useRootStore } from '../../stores/RootStore'
import { observer } from 'mobx-react-lite'
import { hydrateValues } from './utils'
import { SelectPropertyValue } from '../../api/clients/PropertyClient'
import { PropertyValuePill } from '../PropertyValuePill/PropertyValuePill'
import { useReviewConfig } from './ReviewConfigProvider'
import { Portal } from '../Portal/Portal'
import { type ModalPortals } from './CreateReviewActionConfigModal'
import { Tooltip } from '../Tooltip/Tooltip'
import { ConditionalWrapper } from '../../common/ConditionalWrapper/ConditionalWrapper'
import { ArticleFlat } from '../../types'
import { useAuth } from 'hooks/UseAuth'
import { Modal, useModals } from 'almanac-ui/Modal'
import { ConfirmModal } from './ConfirmModal'
import { InformalFeedbackRequestState } from 'api/clients/InformalFeedbackRequestsClient'
import { useInformalFeedbackRequests } from 'hooks/useInformalFeedbackRequests'

const displayText = (value: string | null) => dayjs(value as string).format('ddd, MMM D')

interface Props extends ModalPortals {
  onInviteEmails: () => void
  to: SelectPropertyValue
  connectedTo?: SelectPropertyValue
  article?: ArticleFlat
  onClose: () => void
}

export const ConfigureReview = observer(
  ({ header, footer, to, connectedTo, onInviteEmails, article, onClose }: Props) => {
    const {
      setReviewerValues,
      setApproverValues,
      setViewerValues,
      reviewers,
      approvers,
      viewers,
      reviewerRoles,
      setReviewerRoles,
      htmlMessage,
      setHtmlMessage,
      onSubmit,
      endsOn,
      setEndsOn,
      errors,
      title,
      canAddReviewers,
    } = useReviewConfig()
    const rootStore = useRootStore()
    const sourceData = useSourceData({ article })
    const [proseState] = useState(() => new ProseState())
    const auth = useAuth()
    const { openModal } = useModals()
    const reviewsData = useInformalFeedbackRequests(article?.id || '', article?.main_layer.id || '', {
      states: [InformalFeedbackRequestState.Open],
    })

    const hasNewEmails = [...reviewers, ...approvers, ...viewers].some(
      (reviewer) => reviewer.type === RecipientsSelectorValueType.Email,
    )

    const handleSubmit = () => {
      if (hasNewEmails) {
        return onInviteEmails()
      }
      onSubmit(false)
      onClose()
    }

    return (
      <>
        <Portal container={header}>
          <ModalTitle>
            <div className={Styles.titleWrapper}>
              Start Review and Move to <PropertyValuePill propertyValue={to} />
            </div>
          </ModalTitle>
        </Portal>
        <SourceDataProvider sourceData={sourceData}>
          <ReviewersFields
            reviewers={hydrateValues(
              reviewers.filter(({ value }) => value !== auth?.id),
              rootStore,
            )}
            onReviewersChange={setReviewerValues}
            approvers={hydrateValues(
              approvers.filter(({ value }) => value !== auth?.id),
              rootStore,
            )}
            onApproversChange={setApproverValues}
            viewers={hydrateValues(
              viewers.filter(({ value }) => value !== auth?.id),
              rootStore,
            )}
            onViewersChange={setViewerValues}
            roles={reviewerRoles}
            reviewerRolesButtons={
              <>
                {reviewerRoles.length < 3 && canAddReviewers && (
                  <div className={Styles.roleButtons}>
                    {!reviewerRoles.includes(ReviewerRole.REVIEWER) && canAddReviewers && (
                      <AddReviewerButton
                        onPress={() => setReviewerRoles((roles) => [...roles, ReviewerRole.REVIEWER])}
                      />
                    )}
                    {!reviewerRoles.includes(ReviewerRole.APPROVER) && canAddReviewers && (
                      <AddApproverButton
                        onPress={() => setReviewerRoles((roles) => [...roles, ReviewerRole.APPROVER])}
                      />
                    )}
                    {!reviewerRoles.includes(ReviewerRole.VIEWER) && (
                      <AddViewerButton onPress={() => setReviewerRoles((roles) => [...roles, ReviewerRole.VIEWER])} />
                    )}
                  </div>
                )}
              </>
            }
          />
        </SourceDataProvider>
        <span className={AddReviewersStyles.fieldLabel}>Due date</span>
        <Field focusable={false} className={AddReviewersStyles.dueDateField}>
          <div
            className={cx(AddReviewersStyles.fieldInput, AddReviewersStyles.dueDateField, Styles.schedule)}
            onClick={stopPropagation}
          >
            <div className={Styles.scheduleField}>
              <BaseDueDatePicker
                value={endsOn}
                onChange={(value) => setEndsOn(value as string)}
                displayText={displayText}
                minValue={dayjs().format('YYYY-MM-DD')}
                shape="squared"
                size="lg"
                variant="tertiary"
                className={AddReviewersStyles.dueDatePicker}
              />
            </div>

            {connectedTo && (
              <div className={Styles.scheduleField}>
                <span>When it ends, file moves to</span>
                <PropertyValuePill propertyValue={connectedTo} />
              </div>
            )}
          </div>
        </Field>
        <label htmlFor="message" className={AddReviewersStyles.fieldLabel}>
          Message
        </label>
        <Field className={AddReviewersStyles.messageField}>
          <ProseStateProvider state={proseState}>
            <Toolbar.Root className={AddReviewersStyles.toolbar}>
              <Toolbar.Bold />
              <Toolbar.Italic />
              <Toolbar.Underline />
              <Toolbar.InlineCode />
              <Toolbar.OrderedList />
              <Toolbar.BulletList />
              <Toolbar.Mention />
              <Toolbar.Emoji />
            </Toolbar.Root>
            <ProseInput
              placeholder="Type your message here..."
              className={cx(AddReviewersStyles.fieldInput, AddReviewersStyles.message)}
              initialHtmlString={htmlMessage || ''}
              onChange={(html) => {
                setHtmlMessage(html)
              }}
            />
          </ProseStateProvider>
        </Field>
        <Portal container={footer}>
          <Modal.Footer>
            <Modal.FooterContent slot="right">
              <ConditionalWrapper
                condition={errors.length > 0}
                wrapper={(children) => (
                  <Tooltip label={errors.join('\n')}>
                    <div>{children}</div>
                  </Tooltip>
                )}
              >
                <Modal.FooterButton
                  isDisabled={errors.length > 0 || !title}
                  onPress={() =>
                    !!reviewsData?.informalFeedbackRequests?.length
                      ? openModal(ConfirmModal, {
                          onSubmit: handleSubmit,
                          onClose,
                        })
                      : handleSubmit()
                  }
                  size="lg"
                  variant="primary"
                >
                  Send Request & Move to {to.value}
                </Modal.FooterButton>
              </ConditionalWrapper>
            </Modal.FooterContent>
          </Modal.Footer>
        </Portal>
      </>
    )
  },
)
